import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Plot from 'react-plotly.js';

import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import type { Config, Data, Layout } from 'plotly.js';

import type { PatternCandle } from '@/entities/pattern-candles';

import { formatPatternDate } from '@/shared/lib/datetime';
import { getTimeframeInMinutes } from '@/shared/lib/financial';
import { useResizeObserver, useTheme } from '@/shared/lib/hooks';
import { TimeframeType } from '@/shared/types/timeframe';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { TitleWithHelp } from '@/shared/ui/title-with-help';

import cls from './pattern-visualization.module.css';

// Function to get date format based on timeframe
const getDateFormat = (tf: TimeframeType): string => {
    switch (tf) {
        case TimeframeType.M1:
        case TimeframeType.M5:
        case TimeframeType.M15:
        case TimeframeType.M30:
            return '%H:%M'; // Hour:minute format for minute-based timeframes
        case TimeframeType.H1:
        case TimeframeType.H4:
            return '%d %b %H:%M'; // Day month hour:minute format for hour-based timeframes
        case TimeframeType.D1:
            return '%d %b %Y'; // Day month year format for day-based timeframes
        default:
            return '%Y-%m-%d %H:%M:%S';
    }
};

// Component for pattern pagination
const PatternPagination = memo(
    ({
        currentIndex,
        totalCount,
        onNavigate,
    }: {
        currentIndex: number;
        totalCount: number;
        onNavigate: (direction: 'prev' | 'next') => void;
    }) => {
        const { t } = useTranslation(['table']);

        return (
            <div className={cls.customPagination}>
                <Button
                    aria-label={t('pagination.prev')}
                    disabled={currentIndex === 0}
                    icon={<LeftOutlined />}
                    onClick={() => onNavigate('prev')}
                    variant="secondary"
                />

                <span className={cls.paginationPage}>
                    {currentIndex + 1} / {totalCount}
                </span>

                <Button
                    aria-label={t('pagination.next')}
                    disabled={currentIndex === totalCount - 1}
                    icon={<RightOutlined />}
                    onClick={() => onNavigate('next')}
                    variant="secondary"
                />
            </div>
        );
    },
);

PatternPagination.displayName = 'PatternPagination';

interface PatternVisualizationProps {
    candles: PatternCandle[] | undefined;
    timeframe: TimeframeType;
    helpId?: string;
    isLoading: boolean;
    title?: string;
}

// Type for hover label style
type HoverLabelStyle = {
    font: { color: string };
    bgcolor: string;
};

// Function to create Plotly data from candle data
const createPlotlyData = (
    candleData: PatternCandle['data'],
    startTime: string,
    timeframe: TimeframeType,
    hoverLabelStyle: HoverLabelStyle,
): Data[] => [
    {
        x: candleData.map((_, i) => {
            // Create sequential timestamps from start_time
            const date = new Date(startTime);

            // Calculate time increment based on timeframe
            const timeInMinutes = getTimeframeInMinutes(timeframe);

            // Add incrementation based on index and timeframe
            date.setMinutes(date.getMinutes() + i * timeInMinutes);

            return date.toISOString();
        }),
        open: candleData.map((candle) => candle.open),
        high: candleData.map((candle) => candle.high),
        low: candleData.map((candle) => candle.low),
        close: candleData.map((candle) => candle.close),
        type: 'candlestick' as const,
        increasing: { line: { color: '#26a69a' } },
        decreasing: { line: { color: '#ef5350' } },
        hoverlabel: hoverLabelStyle,
        showlegend: false,
    },
];

// Component for rendering candle chart
const CandleChart = memo(
    ({
        plotlyData,
        layout,
        config,
        size,
        title,
        needPagination,
    }: {
        plotlyData: Data[];
        layout: Partial<Layout>;
        config: Partial<Config>;
        size: { width?: number; height?: number };
        title?: string | undefined;
        needPagination: boolean;
    }) => (
        <Plot
            config={{
                ...config,
                responsive: true,
            }}
            data={plotlyData}
            layout={
                size.width && size.height
                    ? {
                          ...layout,
                          width: size.width,
                          height:
                              size.height -
                              (title ? 40 : 20) -
                              (needPagination ? 24 : 2),
                          autosize: true,
                      }
                    : layout
            }
            style={{
                width: '100%',
                height: '100%',
            }}
            useResizeHandler
        />
    ),
);

CandleChart.displayName = 'CandleChart';

export const PatternVisualization = memo(
    ({
        candles,
        timeframe,
        helpId,
        isLoading,
        title,
    }: PatternVisualizationProps) => {
        const { t } = useTranslation('pattern-studio');
        const [currentIndex, setCurrentIndex] = useState(0);
        const chartRef = useRef<HTMLDivElement>(null);

        const isDarkTheme = useTheme()[2];

        const { ref: resizableComponentRef, size } =
            useResizeObserver<HTMLDivElement>();

        // Use only provided candles (no more default/mock)
        const patternsToDisplay = useMemo(() => {
            if (!candles || candles.length === 0) {
                return [];
            }

            // Use only real data without mocking
            return candles;
        }, [candles]);

        // Navigation handler
        const handleNavigation = (direction: 'prev' | 'next') => {
            setCurrentIndex((prev) => {
                if (direction === 'prev') {
                    return prev > 0 ? prev - 1 : prev;
                }

                return prev < patternsToDisplay.length - 1 ? prev + 1 : prev;
            });
        };

        // Common hover label style
        const hoverLabelStyle: HoverLabelStyle = {
            font: { color: '#FFFFFF' },
            bgcolor: 'rgba(50, 50, 50, 0.8)',
        };

        useEffect(() => {
            setCurrentIndex(0);
        }, [candles, isLoading]);

        if (isLoading) {
            return t('loading');
        }

        if (!patternsToDisplay || patternsToDisplay.length === 0) {
            return <div className={cls.noData}>{t('noPatternsAvailable')}</div>;
        }

        const currentCandle = patternsToDisplay[currentIndex];

        const needPagination = patternsToDisplay.length > 1;

        if (!currentCandle) {
            return (
                <div className={cls.noData}>{t('patternDataNotAvailable')}</div>
            );
        }

        // Transform candle data for plotly
        const candleData = currentCandle.data || [];
        const hasData = candleData.length > 0;

        // Create data for the candlestick chart
        const plotlyData = hasData
            ? createPlotlyData(
                  candleData,
                  currentCandle.start_time,
                  timeframe,
                  hoverLabelStyle,
              )
            : [];

        // Grid color matching the border color (white at 10% opacity)
        const gridColor = isDarkTheme
            ? 'rgba(255, 255, 255, 0.1)'
            : 'rgba(0, 0, 0, 0.1)';

        const layout: Partial<Layout> = {
            autosize: true,
            showlegend: false,
            xaxis: {
                showgrid: true,
                zeroline: false,
                rangeslider: {
                    visible: false,
                },
                type: 'date' as const,
                gridcolor: gridColor,
                automargin: true,
                hoverformat: '%Y-%m-%d %H:%M:%S',
                tickformat: getDateFormat(timeframe),
            },
            yaxis: {
                showgrid: true,
                zeroline: false,
                gridcolor: gridColor,
                automargin: true,
            },
            margin: { t: 5, l: 30, r: 5, b: 15, pad: 0 },
            paper_bgcolor: 'transparent',
            plot_bgcolor: 'transparent',
            hoverlabel: hoverLabelStyle,
            hovermode: 'closest' as const,
        };

        const config: Partial<Config> = {
            displayModeBar: false,
            responsive: true,
            staticPlot: false,
            scrollZoom: false,
            modeBarButtonsToRemove: [
                'zoom2d',
                'pan2d',
                'zoomIn2d',
                'zoomOut2d',
                'autoScale2d',
                'resetScale2d',
                'select2d',
                'lasso2d',
            ] as const,
            toImageButtonOptions: {
                filename: 'pattern_chart',
                format: 'png' as const,
                height: 600,
                scale: 1,
                width: 700,
            },
        };

        return (
            <Card
                className={cls.inner}
                isChild
                isWithoutPadding
            >
                {hasData && <div />}
                <div
                    ref={resizableComponentRef}
                    className={cls.root}
                >
                    {title && (
                        <TitleWithHelp
                            helpId={helpId}
                            title={
                                hasData
                                    ? `${title} - ${formatPatternDate(currentCandle.start_time)}`
                                    : title
                            }
                        />
                    )}
                    <div
                        className={cls.content}
                        style={{
                            flex: '1 1 auto',
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: 0,
                            height: '100%',
                        }}
                    >
                        {hasData ? (
                            <div
                                ref={chartRef}
                                className={cls.chart}
                            >
                                <CandleChart
                                    config={config}
                                    layout={layout}
                                    needPagination={needPagination}
                                    plotlyData={plotlyData}
                                    size={size}
                                    title={title}
                                />
                            </div>
                        ) : (
                            <div className={cls.noData}>
                                {t('candleDataNotAvailable')}
                            </div>
                        )}
                    </div>

                    {needPagination && (
                        <PatternPagination
                            currentIndex={currentIndex}
                            onNavigate={handleNavigation}
                            totalCount={patternsToDisplay.length}
                        />
                    )}
                </div>
            </Card>
        );
    },
);

PatternVisualization.displayName = 'PatternVisualization';
